import { SalaryCalculation } from '../types';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

type AutoTableDocument = jsPDF & {
  autoTable: (options: {
    startY: number;
    head: string[][];
    body: string[][];
    theme: string;
    styles: { fontSize: number };
    headStyles: { fillColor: number[] };
  }) => void;
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCurrencyForPDF = (amount: number) => {
  return `Rs. ${new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(amount)}`;
};

const getEpfModeLabel = (mode: SalaryCalculation['epf']['wageMode']) => {
  switch (mode) {
    case 'basicDa':
      return 'Actual Basic + DA';
    case 'labourCode':
      return 'Labour-code wage floor';
    case 'custom':
      return 'Custom PF wage';
    case 'capped':
    default:
      return 'Capped wage';
  }
};

const benefitRows = (calculation: SalaryCalculation, format: (amount: number) => string): string[][] => {
  const benefits = calculation.benefits;
  if (!benefits || !(benefits.employeeNps || benefits.employerNps || benefits.mealCoupons)) return [];
  return [
    ['NPS AND MEAL BENEFITS', ''],
    ['Employee NPS (payroll deduction)', format(benefits.employeeNps)],
    ['Employer NPS (within CTC; excluded from bank salary)', format(benefits.employerNps)],
    ['Employer NPS tax deduction', format(benefits.employerNpsDeduction)],
    ['Meal coupons (within CTC; excluded from bank salary)', format(benefits.mealCoupons)],
    ['Meal coupon tax exemption', format(benefits.mealExemption)],
    ['Benefits allocated from Special Allowance', 'Already included in Gross Salary'],
  ];
};

export const downloadAsExcel = (
  calculation: SalaryCalculation,
  frequency: 'monthly' | 'yearly',
  ctc: number,
  showTaxDetails: boolean
) => {
  const data = [
    ['CTC CALCULATOR - SALARY BREAKDOWN'],
    [],
    [`Total CTC`, formatCurrency(ctc)],
    [],
    ['EARNINGS'],
    ['Basic Salary', formatCurrency(calculation.basic)],
    ['HRA', formatCurrency(calculation.hra)],
    ['DA', formatCurrency(calculation.da)],
    ['LTA', formatCurrency(calculation.lta)],
    ['Special Allowance', formatCurrency(calculation.special)],
    ['Performance Bonus', formatCurrency(calculation.bonus)],
    ...(calculation.benefits?.employerNps ? [['Employer NPS (within CTC)', formatCurrency(calculation.benefits.employerNps)]] : []),
    ...(calculation.benefits?.mealCoupons ? [['Meal coupons (within CTC)', formatCurrency(calculation.benefits.mealCoupons)]] : []),
    ['Gross Salary', formatCurrency(calculation.grossSalary)],
    [],
    ['DEDUCTIONS'],
    ['PF Wage Base', calculation.epf.applicable ? `${formatCurrency(calculation.epf.wageBase)} (${getEpfModeLabel(calculation.epf.wageMode)})` : 'Not Opted'],
    [`EPF Employee (${calculation.epf.employeeRate}%)`, formatCurrency(calculation.epf.employee)],
    [`EPF Employer (${calculation.epf.employerRate}%)`, formatCurrency(calculation.epf.employer)],
    ['Professional Tax', formatCurrency(calculation.pt)],
    ['ESI', formatCurrency(calculation.esi.total)],
    ...benefitRows(calculation, formatCurrency),
    [],
    ['INCOME TAX BREAKDOWN'],
    ['Total Income Tax', formatCurrency(calculation.tax.total)]
  ];

  if (showTaxDetails) {
    data.push(
      ['Base Tax', formatCurrency(calculation.tax.base)],
      ...(calculation.tax.surcharge > 0 ? [['Surcharge', formatCurrency(calculation.tax.surcharge)]] : []),
      ...(calculation.tax.marginalRelief > 0 ? [['Marginal Relief', `-${formatCurrency(calculation.tax.marginalRelief)}`]] : []),
      ['Health & Education Cess', formatCurrency(calculation.tax.cess)]
    );
  }

  data.push(
    [],
    ['SUMMARY'],
    ['Total Deductions', formatCurrency(calculation.totalDeductions)],
    [`Net ${frequency === 'monthly' ? 'Monthly' : 'Annual'} Salary`, formatCurrency(calculation.netSalary)]
  );

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  XLSX.utils.book_append_sheet(wb, ws, 'Salary Breakdown');
  XLSX.writeFile(wb, 'salary_breakdown.xlsx');
};

export const downloadAsPDF = (
  calculation: SalaryCalculation,
  frequency: 'monthly' | 'yearly',
  ctc: number,
  showTaxDetails: boolean
) => {
  const doc = new jsPDF();
  
  doc.setFontSize(16);
  doc.text('CTC CALCULATOR - SALARY BREAKDOWN', 20, 20);
  doc.setFontSize(12);
  doc.text(`Total CTC: ${formatCurrencyForPDF(ctc)} per ${frequency === 'monthly' ? 'month' : 'year'}`, 20, 30);

  const data: string[][] = [
    ['EARNINGS', ''],
    ['Basic Salary', formatCurrencyForPDF(calculation.basic)],
    ['HRA', formatCurrencyForPDF(calculation.hra)],
    ['DA', formatCurrencyForPDF(calculation.da)],
    ['LTA', formatCurrencyForPDF(calculation.lta)],
    ['Special Allowance', formatCurrencyForPDF(calculation.special)],
    ['Performance Bonus', formatCurrencyForPDF(calculation.bonus)],
    ...(calculation.benefits?.employerNps ? [['Employer NPS (within CTC)', formatCurrencyForPDF(calculation.benefits.employerNps)]] : []),
    ...(calculation.benefits?.mealCoupons ? [['Meal coupons (within CTC)', formatCurrencyForPDF(calculation.benefits.mealCoupons)]] : []),
    ['Gross Salary', formatCurrencyForPDF(calculation.grossSalary)],
    ['', ''],
    ['DEDUCTIONS', ''],
    ['PF Wage Base', calculation.epf.applicable ? `${formatCurrencyForPDF(calculation.epf.wageBase)} (${getEpfModeLabel(calculation.epf.wageMode)})` : 'Not Opted'],
    [`EPF Employee (${calculation.epf.employeeRate}%)`, formatCurrencyForPDF(calculation.epf.employee)],
    [`EPF Employer (${calculation.epf.employerRate}%)`, formatCurrencyForPDF(calculation.epf.employer)],
    ['Professional Tax', formatCurrencyForPDF(calculation.pt)],
    ['ESI', formatCurrencyForPDF(calculation.esi.total)],
    ...benefitRows(calculation, formatCurrencyForPDF),
    ['Income Tax', formatCurrencyForPDF(calculation.tax.total)]
  ];

  if (showTaxDetails) {
    data.push(
      ['Base Tax', formatCurrencyForPDF(calculation.tax.base)],
      ...(calculation.tax.surcharge > 0 ? [['Surcharge', formatCurrencyForPDF(calculation.tax.surcharge)]] : []),
      ...(calculation.tax.marginalRelief > 0 ? [['Marginal Relief', `-${formatCurrencyForPDF(calculation.tax.marginalRelief)}`]] : []),
      ['Health & Education Cess', formatCurrencyForPDF(calculation.tax.cess)]
    );
  }

  data.push(
    ['', ''],
    ['SUMMARY', ''],
    ['Total Deductions', formatCurrencyForPDF(calculation.totalDeductions)],
    [`Net ${frequency === 'monthly' ? 'Monthly' : 'Annual'} Salary`, formatCurrencyForPDF(calculation.netSalary)]
  );

  (doc as AutoTableDocument).autoTable({
    startY: 40,
    head: [['Component', 'Amount']],
    body: data,
    theme: 'striped',
    styles: { fontSize: 10 },
    headStyles: { fillColor: [66, 66, 66] }
  });

  doc.save('salary_breakdown.pdf');
}; 
